import { devLog, devError } from '../utils/devLogger';
import { isRealUser } from '../utils/userFilters';
import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Modal, Form, Alert, Pagination } from 'react-bootstrap';
import EnhancedUserSecurity from '../components/EnhancedUserSecurity';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { exportUserReportToPDF } from '../utils/pdfExport';
import NotificationService from '../services/notificationService';
import ColdStorageService from '../services/coldStorageService';
import { generateUserFriendlyPassword } from '../utils/passwordGenerator';
// ExcelJS is ~1.2 MB — loaded on demand only when the user triggers an export
import AdminNavbar from '../components/AdminNavbar';
import { DOCUMENT_CATEGORIES, checkDocumentDeadline } from '../config/documentCategories';
import { SOUTH_AFRICAN_PROVINCES } from '../constants/addressConstants';
import '../styles/brand.css';

const UserManagement = () => {
  usePageTitle("User Management");
  const { currentUser, userProfile, createUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDocumentStatus, setSelectedDocumentStatus] = useState('all');
  const [selectedEmailStatus, setSelectedEmailStatus] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [allUsers, setAllUsers] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [allCohorts, setAllCohorts] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userModalError, setUserModalError] = useState('');
  const [success, setSuccess] = useState('');
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [securityUser, setSecurityUser] = useState(null);
  const [showCohortTransfer, setShowCohortTransfer] = useState(false);
  const [transferData, setTransferData] = useState({ userId: '', targetCohortId: '', reason: '' });
  const [fixingVerification, setFixingVerification] = useState(false);
  // Bulk cohort assignment state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBulkCohortAssign, setShowBulkCohortAssign] = useState(false);
  const [bulkCohortData, setBulkCohortData] = useState({ programId: '', cohortId: '' });
  const [bulkAssignProgress, setBulkAssignProgress] = useState(0);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [newUser, setNewUser] = useState({
    displayName: '',
    email: '',
    password: '',
    role: 'learner',
    cohortId: '',
    programId: '',
    assignedPrograms: [],
    assignedCohorts: [],
    adminPassword: '',
    // New mandatory fields
    phoneNumber: '',
    alternateNumber: '',
    gender: '',
    race: '',
    idNumber: '',
    dateOfBirth: '',
    province: '',
    workDepartment: '',
    // Address fields
    streetAddress: '',
    city: '',
    postalCode: '',
    // Verification status
    isVerified: false,
    verificationDate: null,
    // Payment status
    paymentStatus: 'pending',
    paymentAmount: 70,
    paymentDate: null
  });
  const [showGoogleSheetsImport, setShowGoogleSheetsImport] = useState(false);
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState('');
  const [sheetsData, setSheetsData] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [importAdminPassword, setImportAdminPassword] = useState('');
  const [importProgramId, setImportProgramId] = useState('');
  const [importCohortId, setImportCohortId] = useState('');
  const [requireAddressOnImport, setRequireAddressOnImport] = useState(false);
  const [csvFileName, setCsvFileName] = useState('');
  const [showCreateProgram, setShowCreateProgram] = useState(false);
  const [newProgramName, setNewProgramName] = useState('');
  const [newProgramDescription, setNewProgramDescription] = useState('');

  // Define status options and reasons locally (streamlined as per feedback)
  const statusOptions = [
    { value: 'active', label: 'Active', variant: 'success' },
    { value: 'inactive', label: 'Inactive', variant: 'secondary' }
  ];

  const statusReasons = {
    active: [], // No reason required for active status
    inactive: [
      'Studying masters',
      'Suspended',
      'Withdrawn',
      'Pregnant',
      'Medical leave',
      'Personal reasons',
      'Financial issues',
      'Technical problems'
    ]
  };

  useEffect(() => {
    const initializeData = async () => {
      await loadUsers();
      // Only load deleted users after regular users are loaded
      setTimeout(() => loadDeletedUsers(), 1000);
    };
    
    initializeData();
    
    // Handle URL parameters
    const filterParam = searchParams.get('filter');
    if (filterParam === 'overdue') {
      setSelectedDocumentStatus('overdue');
    }
  }, [searchParams]);

  useEffect(() => {
    filterUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, selectedStatus, selectedDocumentStatus, selectedEmailStatus, selectedProgram, selectedCohort, searchTerm, allUsers]);

  // Access control - only admins can access user management
  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access user management.</p>
          <Button variant="primary" onClick={() => {
            if (userProfile?.role === 'facilitator') {
              navigate('/facilitator');
            } else {
              navigate('/dashboard');
            }
          }}>
            Go to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  // Load users, programs, and cohorts from Firebase
  const loadUsers = async () => {
    try {
      setLoading(true);

      // Load all data in parallel
      const [usersSnapshot, programsSnapshot, cohortsSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'programs')),
        getDocs(collection(db, 'cohorts'))
      ]);

      // Process users data — exclude load-test accounts
      const usersList = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(isRealUser);

      // Process programs data
      const programsList = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Process cohorts data
      const cohortsList = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      devLog('Loaded User Management Data:', {
        users: usersList.length,
        programs: programsList.length,
        cohorts: cohortsList.length
      });

      setAllUsers(usersList);
      setAllPrograms(programsList);
      setAllCohorts(cohortsList);
    } catch (error) {
      setError('Error loading data: ' + error.message);
      devError('Error loading user management data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load deleted users from cold storage
  const loadDeletedUsers = async () => {
    try {
      // Only try to load deleted users if we have regular users (indicating proper setup)
      if (allUsers.length === 0) {
        devLog('Skipping cold storage load - no users found yet');
        return;
      }
      
      const result = await ColdStorageService.getDeletedRecords('users');
      if (result.success) {
        const deletedUsersList = result.records
          .filter(record => !record.restored && !record.permanentlyDeleted)
          .map(record => ({
            ...record.data,
            coldStorageId: record.id,
            deletedDate: record.deletedAt?.toDate()?.toISOString(),
            deletedBy: record.deletedBy,
            deleteReason: record.deleteReason
          }));
        setDeletedUsers(deletedUsersList);
      }
    } catch (error) {
      devError('Error loading deleted users:', error);
      // Don't show error for cold storage as it's not critical during initial setup
    }
  };

  // Show notification
  const showNotification = (message, type) => {
    if (type === 'success') {
      setSuccess(message);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(message);
      setTimeout(() => setError(''), 3000);
    }
  };

  // Create new program during import
  const handleCreateProgramForImport = async () => {
    if (!newProgramName.trim()) {
      setError('Program name is required');
      return;
    }

    try {
      const programData = {
        name: newProgramName.trim(),
        programName: newProgramName.trim(),
        description: newProgramDescription.trim() || `Program created during bulk import on ${new Date().toLocaleDateString()}`,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        isActive: true,
        enrolledLearners: [],
        totalLearners: 0
      };

      const programRef = await addDoc(collection(db, 'programs'), programData);

      // Add to local state
      const newProgram = {
        id: programRef.id,
        ...programData
      };
      setAllPrograms([...allPrograms, newProgram]);

      // Select the newly created program
      setImportProgramId(programRef.id);

      // Reset form
      setNewProgramName('');
      setNewProgramDescription('');
      setShowCreateProgram(false);

      setSuccess(`Program "${newProgramName}" created successfully!`);
    } catch (error) {
      devError('Error creating program:', error);
      setError('Failed to create program: ' + error.message);
    }
  };

  // Generate random password
  const generatePassword = () => {
    return generateUserFriendlyPassword();
  };

  // Send credentials email
  const sendCredentialsEmail = async (userEmail, password, role, displayName) => {
    try {
      // Import Firebase functions
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const sendScheduledEmail = httpsCallable(functions, 'sendScheduledEmail');

      // Call the Firebase function to send credentials email
      const result = await sendScheduledEmail({
        templateType: 'user_credentials',
        recipients: [userEmail],
        templateData: {
          email: userEmail,
          password: password,
          role: role,
          userName: displayName
        }
      });

      if (result.data.success) {
        devLog('Credentials email sent successfully:', result.data);
        return { success: true };
      } else {
        devError('Failed to send credentials email:', result.data);
        return { success: false, error: 'Failed to send email' };
      }
    } catch (error) {
      devError('Error sending credentials email:', error);
      return { success: false, error: error.message };
    }
  };

  // Google Sheets import functionality (CSV functions removed)

  // Check if user has overdue documents
  const hasOverdueDocuments = (user) => {
    if (user.role !== 'learner') return false;
    
    const registrationDate = user.enrolledDate || user.createdAt;
    if (!registrationDate) return false;
    
    const deadlineInfo = checkDocumentDeadline(registrationDate);
    if (!deadlineInfo.isOverdue) return false;
    
    // Check if user has submitted all required documents
    const requiredCategories = Object.keys(DOCUMENT_CATEGORIES).filter(
      key => DOCUMENT_CATEGORIES[key].mandatory
    );
    
    const userDocuments = user.documents || {};
    const missingDocuments = requiredCategories.filter(
      category => !userDocuments[category] || !userDocuments[category].fileURL
    );
    
    return missingDocuments.length > 0;
  };
  
  // Get document completion status for user
  const getDocumentCompletionStatus = (user) => {
    if (user.role !== 'learner') return { completed: 0, total: 0, percentage: 0 };
    
    const requiredCategories = Object.keys(DOCUMENT_CATEGORIES).filter(
      key => DOCUMENT_CATEGORIES[key].mandatory
    );
    
    const userDocuments = user.documents || {};
    const completedDocs = requiredCategories.filter(
      category => userDocuments[category] && userDocuments[category].fileURL
    );
    
    return {
      completed: completedDocs.length,
      total: requiredCategories.length,
      percentage: Math.round((completedDocs.length / requiredCategories.length) * 100)
    };
  };
  
  const hasLoggedIn = (user) => Boolean(user?.lastLoginAt || user?.firstLoginAt);

  const isInactiveUser = (user) => {
    if (!user) return false;
    if (user.isActive === false) return true;
    const status = (user.status || '').toLowerCase();
    return ['inactive', 'dropoff', 'dropped'].includes(status);
  };

  const isActiveUser = (user) => !isInactiveUser(user);

  const filterUsers = () => {
    let filtered = [...allUsers];

    if (selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    if (selectedStatus !== 'all') {
      if (selectedStatus === 'inactive') {
        filtered = filtered.filter(user => isInactiveUser(user));
      } else if (selectedStatus === 'active') {
        filtered = filtered.filter(user => isActiveUser(user));
      } else {
        filtered = filtered.filter(user => user.status === selectedStatus);
      }
    }
    
    if (selectedDocumentStatus !== 'all') {
      if (selectedDocumentStatus === 'overdue') {
        filtered = filtered.filter(user => hasOverdueDocuments(user));
      } else if (selectedDocumentStatus === 'incomplete') {
        filtered = filtered.filter(user => {
          const status = getDocumentCompletionStatus(user);
          return user.role === 'learner' && status.percentage < 100;
        });
      } else if (selectedDocumentStatus === 'complete') {
        filtered = filtered.filter(user => {
          const status = getDocumentCompletionStatus(user);
          return user.role === 'learner' && status.percentage === 100;
        });
      }
    }

    if (selectedEmailStatus !== 'all') {
      if (selectedEmailStatus === 'verified') {
        filtered = filtered.filter(user => hasLoggedIn(user));
      } else if (selectedEmailStatus === 'unverified') {
        filtered = filtered.filter(user => !hasLoggedIn(user));
      }
    }

    if (selectedProgram !== 'all') {
      filtered = filtered.filter(user => user.programId === selectedProgram);
    }

    if (selectedCohort !== 'all') {
      filtered = filtered.filter(user => {
        const cohortIds = new Set([
          user.cohortId,
          ...(Array.isArray(user.cohortIds) ? user.cohortIds : []),
          ...(Array.isArray(user.assignedCohorts) ? user.assignedCohorts : []),
          ...(Array.isArray(user.managedCohorts) ? user.managedCohorts : [])
        ].filter(Boolean));
        return cohortIds.has(selectedCohort);
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification('Password copied to clipboard!', 'success');
    }).catch(() => {
      showNotification('Failed to copy password', 'error');
    });
  };

  // Check if email already exists
  const checkEmailExists = async (email) => {
    return allUsers.some(user => 
      user.email.toLowerCase() === email.toLowerCase()
    );
  };

  const handleAddUser = async () => {
    setUserModalError('');
    if (!newUser.displayName || !newUser.email) {
      setUserModalError('Please fill in name and email fields.');
      return;
    }

    if (!isAdmin()) {
      setUserModalError('Only administrators can create users.');
      return;
    }

    // Check if email already exists
    const emailExists = await checkEmailExists(newUser.email);
    if (emailExists) {
      setUserModalError(`A user with email "${newUser.email}" already exists. Please use a different email address.`);
      return;
    }

    try {
      setLoading(true);
      
      // Auto-generate password if not provided
      const password = newUser.password || generatePassword();
      
      const userData = {
        ...newUser,
        password: password,
        tempPassword: password, // Store temporarily for email trigger
        needsCredentialsEmail: true, // Flag for Firebase trigger
        emailVerified: false, // New users start as unverified
        emailSentAt: new Date().toISOString() // Track when credentials were sent
      };
      
      await createUser(userData);
      
      // Store credentials for display
      setNewUserCredentials({
        name: userData.displayName,
        email: userData.email,
        password: password,
        role: userData.role,
        createdAt: new Date().toLocaleString()
      });
      
      showNotification(
        `User "${userData.displayName}" created successfully! Credentials email will be sent automatically to ${userData.email}`, 
        'success'
      );
      
      // Reload users to show the new one
      await loadUsers();
      
      setNewUser({
        displayName: '',
        email: '',
        password: '',
        role: 'learner',
        cohortId: '',
        programId: '',
        adminPassword: '',
        // New mandatory fields
        alternateNumber: '',
        gender: '',
        race: '',
        idNumber: '',
        dateOfBirth: '',
        province: '',
        workDepartment: '',
        // Verification status
        isVerified: false,
        verificationDate: null,
        // Payment status
        paymentStatus: 'pending',
        paymentAmount: 70,
        paymentDate: null
      });
      setShowUserModal(false);
      setUserModalError('');
    } catch (error) {
      devError('Error creating user:', error);
      setUserModalError('Error creating user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    exportUserReportToPDF(filteredUsers, 'User Management Report');
  };

  const formatDateForExport = (value) => {
    if (!value) return '';
    try {
      if (typeof value?.toDate === 'function') {
        return value.toDate().toLocaleString();
      }
      if (value instanceof Date) {
        return value.toLocaleString();
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    } catch {
      return String(value);
    }
  };

  const getUserCohortNames = (user) => {
    const ids = [
      user.cohortId,
      ...(Array.isArray(user.cohortIds) ? user.cohortIds : []),
      ...(Array.isArray(user.assignedCohorts) ? user.assignedCohorts : []),
      ...(Array.isArray(user.managedCohorts) ? user.managedCohorts : [])
    ].filter(Boolean);
    const uniqueIds = [...new Set(ids)];
    if (uniqueIds.length === 0) return '';
    return uniqueIds.map((id) => getCohortName(id)).join(', ');
  };

  const handleExportFilteredSpreadsheet = async () => {
    const rows = filteredUsers.map((user) => {
      const progress = getUserProgress(user.uid);
      const docStatus = getDocumentCompletionStatus(user);
      const programNames = [
        user.programId ? getProgramName(user.programId) : null,
        ...(Array.isArray(user.assignedPrograms) ? user.assignedPrograms.map(pid => getProgramName(pid)) : [])
      ].filter(Boolean);

      return {
        Name: user.displayName || '',
        Email: user.email || '',
        Phone: user.phoneNumber || user.alternateNumber || '',
        Role: user.role || '',
        Status: user.status || (isInactiveUser(user) ? 'inactive' : 'active'),
        'Status Reason': user.statusReason || '',
        Program: [...new Set(programNames)].join(', '),
        Cohort: getUserCohortNames(user),
        'Progress %': user.role === 'learner' ? progress.progress : '',
        'Progress Completed': user.role === 'learner' ? progress.completed : '',
        'Progress Enrolled': user.role === 'learner' ? progress.enrolled : '',
        'Documents Completed': user.role === 'learner' ? docStatus.completed : '',
        'Documents Total': user.role === 'learner' ? docStatus.total : '',
        'Documents %': user.role === 'learner' ? docStatus.percentage : '',
        Joined: formatDateForExport(user.createdAt),
        'Last Login': formatDateForExport(user.lastLoginAt || user.firstLoginAt)
      };
    });

    if (rows.length === 0) {
      showNotification('No filtered users to export', 'error');
      return;
    }

    try {
      const { default: ExcelJS } = await import('exceljs');
      const headers = Object.keys(rows[0]);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('User Management', {
        views: [{ state: 'frozen', ySplit: 1 }]
      });

      worksheet.addRow(headers);
      rows.forEach((row) => {
        worksheet.addRow(headers.map((header) => row[header]));
      });

      worksheet.columns = headers.map((header) => ({
        header,
        key: header,
        width: Math.min(Math.max(header.length + 4, 14), 28)
      }));

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9EAD3' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.alignment = { vertical: 'top', wrapText: true };
          });
        }
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
          };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user-management-filtered-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      devError('Error exporting filtered spreadsheet:', error);
      showNotification(`Error exporting spreadsheet: ${error.message}`, 'error');
    }
  };

  // Bulk fix learner verification status - ONLY for learners who have logged in
  const handleFixLearnerVerification = async () => {
    if (!window.confirm('This will mark learners who have logged in as verified (emailVerified and isVerified = true). Only learners with a login record will be updated. Continue?')) {
      return;
    }

    setFixingVerification(true);
    setError('');
    setSuccess('');

    try {
      // Get all learners
      const usersRef = collection(db, 'users');
      const learnersQuery = query(usersRef, where('role', '==', 'learner'));
      const learnersSnapshot = await getDocs(learnersQuery);

      devLog(`Found ${learnersSnapshot.size} learners to process`);

      let updatedCount = 0;
      let alreadyVerifiedCount = 0;
      let neverLoggedInCount = 0;

      // Process in batches of 500 (Firestore limit)
      const batchSize = 500;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const docSnap of learnersSnapshot.docs) {
        const userData = docSnap.data();

        // ONLY process learners who have logged in (have lastLoginAt)
        if (!userData.lastLoginAt) {
          neverLoggedInCount++;
          continue;
        }

        // Check if already verified
        if (userData.emailVerified === true && userData.isVerified === true) {
          alreadyVerifiedCount++;
          continue;
        }

        const updateData = {
          emailVerified: true,
          isVerified: true,
          updatedAt: serverTimestamp()
        };

        // Set emailVerifiedAt if not already set
        if (!userData.emailVerifiedAt) {
          updateData.emailVerifiedAt = userData.lastLoginAt;
        }

        // Set firstLoginAt if not already set
        if (!userData.firstLoginAt) {
          updateData.firstLoginAt = userData.lastLoginAt;
        }

        batch.update(docSnap.ref, updateData);
        batchCount++;
        updatedCount++;

        // Commit batch when it reaches the limit
        if (batchCount >= batchSize) {
          await batch.commit();
          devLog(`Committed batch of ${batchCount} updates`);
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
        devLog(`Committed final batch of ${batchCount} updates`);
      }

      setSuccess(`Verification fix complete! Updated: ${updatedCount} learners. Already verified: ${alreadyVerifiedCount}. Never logged in: ${neverLoggedInCount}.`);

      // Reload users to reflect changes
      await loadUsers();
    } catch (err) {
      devError('Error fixing verification:', err);
      setError('Failed to fix verification: ' + err.message);
    } finally {
      setFixingVerification(false);
    }
  };

  const handleEditUser = (user) => {
    setUserModalError('');
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleSecuritySettings = (user) => {
    setSecurityUser(user);
    setShowSecurityModal(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleSaveUser = async () => {
    if (editingUser) {
      try {
        setLoading(true);

        const originalUser = allUsers.find(user => user.uid === editingUser.uid);

        // Prepare the updated user data
        const updatedUserData = {
          ...editingUser,
          updatedAt: serverTimestamp(),
          lastModifiedBy: currentUser.uid
        };

        // Update in Firebase using the document ID
        const userDocRef = doc(db, 'users', editingUser.id);
        await updateDoc(userDocRef, updatedUserData);

        // Update local state
        const updatedUsers = allUsers.map(user =>
          user.uid === editingUser.uid ? { ...user, ...updatedUserData } : user
        );

        // Send notifications if status changed for learners
        if (originalUser && originalUser.status !== editingUser.status && editingUser.role === 'learner') {
          NotificationService.notifyStudentStatusChanged(editingUser, originalUser.status, editingUser.status);
        }

        // Send notification if program assignment changed
        if (originalUser && originalUser.programId !== editingUser.programId && editingUser.role === 'learner') {
          const programName = getProgramName(editingUser.programId);
          NotificationService.notifyStudentProgramAssigned(editingUser, programName);
        }

        setAllUsers(updatedUsers);
        setShowUserModal(false);
        setUserModalError('');
        setEditingUser(null);
        showNotification(`User ${editingUser.displayName} updated successfully. Program: ${getProgramName(editingUser.programId) || 'None'}`, 'success');

        devLog('User updated in Firebase:', {
          userId: editingUser.id,
          name: editingUser.displayName,
          program: getProgramName(editingUser.programId),
          cohort: getCohortName(editingUser.cohortId)
        });

      } catch (error) {
        devError('Error updating user:', error);
        setUserModalError('Failed to update user: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      try {
        const result = await ColdStorageService.moveUserToColdStorage(
          userToDelete.uid,
          userToDelete,
          currentUser.uid,
          'Manual deletion by admin'
        );
        
        if (result.success) {
          const updatedUsers = allUsers.filter(user => user.uid !== userToDelete.uid);
          setAllUsers(updatedUsers);
          setShowDeleteModal(false);
          setUserToDelete(null);
          showNotification('User moved to cold storage successfully', 'success');
          
          await loadDeletedUsers();
        } else {
          showNotification('Error deleting user: ' + result.error, 'error');
        }
      } catch (error) {
        showNotification('Error deleting user: ' + error.message, 'error');
      }
    }
  };

  const handleRestoreUser = async (user) => {
    try {
      const result = await ColdStorageService.restoreRecord(user.coldStorageId, currentUser.uid);
      
      if (result.success) {
        await addDoc(collection(db, 'users'), result.data);
        await loadUsers();
        await loadDeletedUsers();
        showNotification('User restored successfully', 'success');
      } else {
        showNotification('Error restoring user: ' + result.error, 'error');
      }
    } catch (error) {
      showNotification('Error restoring user: ' + error.message, 'error');
    }
  };

  const handlePermanentDelete = async (userId) => {
    const userToDelete = deletedUsers.find(u => u.uid === userId);
    if (userToDelete && window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) {
      try {
        const result = await ColdStorageService.permanentlyDelete(
          userToDelete.coldStorageId,
          currentUser.uid,
          'Admin confirmed permanent deletion'
        );
        
        if (result.success) {
          await loadDeletedUsers();
          showNotification('User permanently deleted from cold storage', 'warning');
        } else {
          showNotification('Error permanently deleting user: ' + result.error, 'error');
        }
      } catch (error) {
        showNotification('Error permanently deleting user: ' + error.message, 'error');
      }
    }
  };

  const getUserProgress = (userId) => {
    // TODO: Implement Firebase-based progress tracking
    // For now, return default values
    return { enrolled: 0, completed: 0, progress: 0 };
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'facilitator': return 'warning';
      case 'qa': return 'success';
      case 'assessor': return 'info';
      case 'learner': return 'primary';
      case 'external_facilitator_limited': return 'dark';
      case 'external_viewer_reporting': return 'secondary';
      case 'external_facilitator': return 'dark';
      case 'external_viewer': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'de-registered': return 'danger';
      case 'pregnant-leave': return 'info';
      case 'suspended': return 'dark';
      case 'graduated': return 'primary';
      default: return 'secondary';
    }
  };

  const getCohortName = (cohortId) => {
    const cohort = allCohorts.find(c => c.id === cohortId);
    return cohort ? cohort.name : 'No Cohort';
  };

  const getProgramName = (programId) => {
    const program = allPrograms.find(p => p.id === programId);
    return program ? program.name : 'No Program';
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleCohortTransfer = (userId) => {
    setTransferData({ userId, targetCohortId: '', reason: '' });
    setShowCohortTransfer(true);
  };

  const handleTransferConfirm = async () => {
    const user = allUsers.find(u => u.uid === transferData.userId);
    if (!user) return;

    const targetCohort = allCohorts.find(c => c.id === transferData.targetCohortId);
    const fromCohortName = getCohortName(user.cohortId);
    const toCohortName = getCohortName(transferData.targetCohortId);

    const transferEntry = {
      fromCohortId: user.cohortId || null,
      toCohortId: transferData.targetCohortId,
      reason: transferData.reason,
      date: new Date().toISOString(),
      transferredBy: currentUser.uid
    };

    try {
      await updateDoc(doc(db, 'users', transferData.userId), {
        cohortId: transferData.targetCohortId,
        programId: targetCohort ? targetCohort.programId : user.programId,
        transferHistory: [...(user.transferHistory || []), transferEntry],
        updatedAt: serverTimestamp()
      });

      const updatedUser = {
        ...user,
        cohortId: transferData.targetCohortId,
        programId: targetCohort ? targetCohort.programId : user.programId,
        transferHistory: [...(user.transferHistory || []), transferEntry]
      };
      NotificationService.notifyStudentTransferred(updatedUser, fromCohortName, toCohortName);
      setAllUsers(allUsers.map(u => u.uid === transferData.userId ? updatedUser : u));
      showNotification(`${user.displayName} transferred to ${toCohortName}`, 'success');
    } catch (err) {
      devError('Error transferring user cohort:', err);
      showNotification('Transfer failed: ' + err.message, 'danger');
    }

    setShowCohortTransfer(false);
    setTransferData({ userId: '', targetCohortId: '', reason: '' });
  };

  // Bulk cohort assignment handler
  const handleBulkCohortAssign = async () => {
    if (!bulkCohortData.programId || !bulkCohortData.cohortId) {
      showNotification('Please select both a program and a cohort', 'warning');
      return;
    }

    if (selectedUserIds.length === 0) {
      showNotification('No users selected for bulk assignment', 'warning');
      return;
    }

    setIsBulkAssigning(true);
    setBulkAssignProgress(0);

    const targetCohort = allCohorts.find(c => c.id === bulkCohortData.cohortId);
    const updatedUsers = [...allUsers];
    let successCount = 0;

    for (let i = 0; i < selectedUserIds.length; i++) {
      const userId = selectedUserIds[i];
      const userIndex = updatedUsers.findIndex(u => u.uid === userId);

      if (userIndex !== -1) {
        const user = updatedUsers[userIndex];

        // Only update learners
        if (user.role === 'learner') {
          try {
            // Update in Firestore
            await updateDoc(doc(db, 'users', userId), {
              programId: bulkCohortData.programId,
              cohortId: bulkCohortData.cohortId,
              updatedAt: serverTimestamp()
            });

            // Update local state
            updatedUsers[userIndex] = {
              ...user,
              programId: bulkCohortData.programId,
              cohortId: bulkCohortData.cohortId
            };
            successCount++;
          } catch (err) {
            devError(`Error updating user ${userId}:`, err);
          }
        }
      }

      // Update progress
      setBulkAssignProgress(Math.round(((i + 1) / selectedUserIds.length) * 100));
    }

    setAllUsers(updatedUsers);
    setIsBulkAssigning(false);
    setShowBulkCohortAssign(false);
    setSelectedUserIds([]);
    setBulkCohortData({ programId: '', cohortId: '' });
    setBulkAssignProgress(0);

    showNotification(`Successfully assigned ${successCount} learners to ${targetCohort?.name || 'the selected cohort'}`, 'success');
  };

  // Toggle single user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select/deselect all learners on current page
  const toggleSelectAllLearners = () => {
    const learnersOnPage = filteredUsers
      .slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage)
      .filter(u => u.role === 'learner')
      .map(u => u.uid);

    const allSelected = learnersOnPage.every(id => selectedUserIds.includes(id));

    if (allSelected) {
      // Deselect all learners on this page
      setSelectedUserIds(prev => prev.filter(id => !learnersOnPage.includes(id)));
    } else {
      // Select all learners on this page
      setSelectedUserIds(prev => [...new Set([...prev, ...learnersOnPage])]);
    }
  };

  // ID Number parsing functions
  const parseGenderFromId = (idNumber) => {
    if (!idNumber || idNumber.length < 7) return '';
    const genderDigit = parseInt(idNumber.substring(6, 7));
    return genderDigit >= 0 && genderDigit <= 4 ? 'Female' : 'Male';
  };

  const parseDateOfBirthFromId = (idNumber) => {
    if (!idNumber || idNumber.length < 6) return '';
    const year = parseInt(idNumber.substring(0, 2));
    const month = idNumber.substring(2, 4);
    const day = idNumber.substring(4, 6);
    
    // Assume years 00-30 are 2000s, 31-99 are 1900s
    const fullYear = year <= 30 ? 2000 + year : 1900 + year;
    return `${fullYear}-${month}-${day}`;
  };

  // Auto-parse ID number when it changes
  const handleIdNumberChange = (idNumber) => {
    setNewUser({
      ...newUser,
      idNumber,
      gender: parseGenderFromId(idNumber),
      dateOfBirth: parseDateOfBirthFromId(idNumber)
    });
  };

  const toPascalCase = (value, fallback = '') => {
    if (!value) return fallback;
    return value
      .toString()
      .split(/[^a-zA-Z0-9]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  };

  const sanitizeSegment = (value, fallback) => {
    const input = value?.toString();
    if (!input) return fallback;
    const sanitized = input.replace(/[^a-zA-Z0-9]+/g, '');
    return sanitized || fallback;
  };

  const buildDocumentFilename = (user, document) => {
    const nameSegment =
      toPascalCase(`${user?.firstName || ''} ${user?.lastName || ''}`.trim(), '') ||
      toPascalCase(user?.displayName, 'Learner');
    const idSegment = sanitizeSegment(user?.idNumber, `ID${user?.uid || 'Unknown'}`);
    const docTypeSource = document?.category || document?.categoryName || document?.documentType || 'Document';
    const docTypeSegment = toPascalCase(docTypeSource, 'Document');
    const originalName = document?.fileName || document?.originalFileName || 'document.pdf';
    const extensionMatch = originalName.match(/(\.[a-zA-Z0-9]+)$/);
    const extension = extensionMatch ? extensionMatch[1] : '.pdf';

    return `${nameSegment}_${idSegment}_${docTypeSegment}${extension}`;
  };


  const handleDownloadDocument = (user, document) => {
    if (!user?.uid || !document?.fileName) {
      showNotification('Unable to download document. Missing file reference.', 'danger');
      return;
    }

    const filename = buildDocumentFilename(user, document);
    const link = document.createElement('a');
    link.href = `/api/documents/${user.uid}/${document.fileName}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification(`Downloading ${filename}`, 'info');
  };

  // Google Sheets functionality
  const handleGoogleSheetsImport = async () => {
    if (!googleSheetsUrl.trim()) {
      setError('Please enter a Google Sheets URL');
      return;
    }

    // Clear previous data
    setSheetsData([]);
    setError('');
    setSuccess('');

    try {
      setImportProgress(5);
      setImportStatus('Validating Google Sheets URL...');

      // Validate the Google Sheets URL before sending it to the server-side CSV proxy.
      convertGoogleSheetsUrl(googleSheetsUrl);

      setImportProgress(15);
      setImportStatus('Fetching Google Sheets data (this may take a moment for large sheets)...');

      const idToken = await currentUser.getIdToken();
      const response = await fetch('/api/google-sheets-csv', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify({ url: googleSheetsUrl.trim() })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Failed to fetch Google Sheets data (HTTP ${response.status}). Please check the URL and sharing settings.`);
      }

      setImportProgress(40);
      setImportStatus('Reading sheet content...');

      const { csvText = '' } = await response.json();
      devLog(`Retrieved CSV data: ${csvText.length} characters, ${csvText.split('\n').length} lines`);

      if (!csvText || csvText.trim().length === 0) {
        throw new Error('The sheet appears to be empty or inaccessible.');
      }

      setImportProgress(60);
      setImportStatus('Parsing and validating user data...');

      const parsedData = parseGoogleSheetsCSV(csvText);

      setImportProgress(85);
      setImportStatus('Validating user data...');

      // Additional validation
      if (parsedData.length === 0) {
        throw new Error('No valid user data found in the sheet.');
      }

      if (parsedData.length > 300) {
        throw new Error(`Too many users (${parsedData.length}). Maximum supported is 300 users per import.`);
      }

      setSheetsData(parsedData);

      setImportProgress(100);
      setImportStatus(`Ready to import ${parsedData.length} users`);
      setSuccess(`Successfully parsed ${parsedData.length} users from the Google Sheet. Review the data below and click "Process Import" to continue.`);

    } catch (error) {
      devError('Google Sheets import error:', error);
      setError('Google Sheets import failed: ' + error.message);
      setImportProgress(0);
      setImportStatus('');
      setSheetsData([]);
    }
  };

  const handleCsvFileUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a .csv file.');
      return;
    }
    setError('');
    setSuccess('');
    setSheetsData([]);
    setGoogleSheetsUrl('');
    setCsvFileName(file.name);
    try {
      setImportProgress(20);
      setImportStatus('Reading CSV file...');
      const text = await file.text();
      if (!text.trim()) {
        throw new Error('The CSV file is empty.');
      }
      setImportProgress(60);
      setImportStatus('Parsing and validating user data...');
      const parsedData = parseGoogleSheetsCSV(text);
      setImportProgress(100);
      if (parsedData.length === 0) {
        setImportStatus('No valid user rows found. Check column headers match expected format.');
      } else {
        setImportStatus(`Ready to import ${parsedData.length} users from ${file.name}`);
        setSheetsData(parsedData);
      }
    } catch (err) {
      setError('CSV parse error: ' + err.message);
      setImportProgress(0);
      setImportStatus('');
      setCsvFileName('');
    }
  };

  const convertGoogleSheetsUrl = (url) => {
    // Extract the sheet ID from various Google Sheets URL formats
    let sheetId = '';
    
    if (url.includes('/spreadsheets/d/')) {
      const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      if (match) {
        sheetId = match[1];
      }
    }
    
    if (!sheetId) {
      throw new Error('Invalid Google Sheets URL. Please use a valid Google Sheets link.');
    }
    
    // Return CSV export URL
    return `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
  };

  const parseGoogleSheetsCSV = (csvText) => {
    // Split lines and filter out truly empty lines
    const records = parseCSVText(csvText).filter(row => {
      const cleanRow = row.map(cell => String(cell || '').trim()).filter(Boolean);
      return cleanRow.length > 0;
    });

    if (records.length < 2) {
      throw new Error('Sheet appears to be empty or has no data rows');
    }

    const headers = records[0].map(h => String(h || '').trim().replace(/"/g, ''));
    const users = [];

    devLog(`Processing ${records.length - 1} data rows from Google Sheets`);
    
    // Find relevant column indices based on the sheet format you provided
    const columnMap = {
      surname: findColumnIndex(headers, ['surname', 'last name', 'last_name', 'family name']),
      firstName: findColumnIndex(headers, ['name', 'first name', 'first_name', 'given name', 'full names']),
      identityNumber: findColumnIndex(headers, ['identity number', 'id number', 'id']),
      dateOfBirth: findColumnIndex(headers, ['date of birth', 'dob', 'birth date']),
      age: findColumnIndex(headers, ['age']),
      gender: findColumnIndex(headers, ['gender', 'sex']),
      race: findColumnIndex(headers, ['race', 'ethnicity']),
      disability: findColumnIndex(headers, ['disability', 'disability status', 'disability (indicate n/a if it is not applicable)']),
      whatsappNumber: findColumnIndex(headers, ['whatsapp number', 'whatsapp', 'wa number']),
      cellNumber1: findColumnIndex(headers, ['cell number', 'cell number 1', 'mobile', 'phone', 'phone number', 'contact']),
      cellNumber2: findColumnIndex(headers, ['emergency/alternate number', 'cell number 2', 'alternative contact', 'alt phone', 'emergency number', 'alternate number']),
      email: findColumnIndex(headers, ['email address', 'email', 'e-mail']),
      province: findColumnIndex(headers, ['province', 'state']),
      town: findColumnIndex(headers, ['town/city', 'town', 'city', 'location']),
      streetAddress: findColumnIndex(headers, ['home address', 'street address', 'address', 'physical address']),
      postalCode: findColumnIndex(headers, ['postal code', 'zip code', 'postcode', 'zip']),
      programme: findColumnIndex(headers, ['program', 'training programme', 'programme', 'course', 'qualification', 'qualification name', 'b']),
      provider: findColumnIndex(headers, ['provider', 'training provider', 'institute', 'institution']),
      startDate: findColumnIndex(headers, ['start date', 'commencement date', 'start']),
      endDate: findColumnIndex(headers, ['end date', 'completion date', 'end']),
      employmentStatus: findColumnIndex(headers, ['employment status', 'employment']),
      employmentStatus2: findColumnIndex(headers, ['employment status 2']),
      talId: findColumnIndex(headers, ['tal id', 'tal id - lookup function']),
      status: findColumnIndex(headers, ['training status: active', 'status', 'training status'])
    };

    const missingRequiredColumns = [];
    if (columnMap.firstName === -1 && columnMap.surname === -1) {
      missingRequiredColumns.push('First Name / Surname');
    }
    if (columnMap.email === -1) {
      missingRequiredColumns.push('Email Address');
    }
    if (missingRequiredColumns.length > 0) {
      throw new Error(
        `Missing required column(s): ${missingRequiredColumns.join(', ')}. Please verify the sheet headers match the template.`
      );
    }
    
    // Process each data row with enhanced error handling
    let processedCount = 0;
    let skippedCount = 0;

    for (let i = 1; i < records.length && i <= 300; i++) { // Limit to 300 rows max for safety
      try {
        const row = records[i];

        // Skip rows that are obviously empty or have too few columns
        if (row.length < 2 || row.every(cell => !cell || cell.trim() === '')) {
          skippedCount++;
          continue;
        }

        // Enhanced display name handling
        const firstName = (row[columnMap.firstName] || '').trim();
        const surname = (row[columnMap.surname] || '').trim();
        let displayName = '';

        if (firstName && surname) {
          displayName = `${firstName} ${surname}`;
        } else if (firstName) {
          displayName = firstName;
        } else if (surname) {
          displayName = surname;
        } else {
          // Try to extract name from other fields if available
          const potentialName = (row[0] || '').trim();
          if (potentialName && potentialName.length > 2) {
            displayName = potentialName;
          }
        }

        // Skip if no valid name found
        if (!displayName || displayName.length < 2) {
          devLog(`Skipping row ${i}: No valid name found`);
          skippedCount++;
          continue;
        }

        // Enhanced email handling
        let email = (row[columnMap.email] || '').trim().toLowerCase();
        if (!email || !email.includes('@')) {
          // Generate email from name if none provided
          const emailName = displayName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '.')
            .replace(/(^\.|\.$)/g, '');
          const uniqueSuffix = `${Date.now()}${i}`.slice(-6);
          email = `${emailName || 'learner'}.${uniqueSuffix}@npigovernance.co.za`;
        }

        // Extract address fields first to check completeness
        const streetAddress = (row[columnMap.streetAddress] || '').trim();
        let city = (row[columnMap.town] || '').trim();
        let province = (row[columnMap.province] || '').trim();
        if (!province && city.includes(',')) {
          const locationParts = city.split(',').map(part => part.trim()).filter(Boolean);
          city = locationParts[0] || city;
          province = locationParts[1] || '';
        }
        const postalCode = (row[columnMap.postalCode] || '').trim();

        // Check if address is complete - if not, flag for verification on first login
        const hasCompleteAddress = streetAddress && city && province && postalCode;

        const user = {
          displayName: displayName,
          email: email,
          role: 'learner', // Default role for imported users
          idNumber: (row[columnMap.identityNumber] || '').trim(),
          dateOfBirth: (row[columnMap.dateOfBirth] || '').trim(),
          age: (row[columnMap.age] || '').trim(),
          gender: (row[columnMap.gender] || '').trim(),
          race: (row[columnMap.race] || '').trim(),
          disability: (row[columnMap.disability] || '').trim(),
          whatsappNumber: cleanPhoneNumber(row[columnMap.whatsappNumber] || ''),
          phone: cleanPhoneNumber(row[columnMap.cellNumber1] || ''),
          alternatePhone: cleanPhoneNumber(row[columnMap.cellNumber2] || ''),
          province: province,
          city: city,
          streetAddress: streetAddress,
          postalCode: postalCode,
          programme: (row[columnMap.programme] || '').trim(),
          provider: (row[columnMap.provider] || '').trim(),
          startDate: (row[columnMap.startDate] || '').trim(),
          endDate: (row[columnMap.endDate] || '').trim(),
          employmentStatus: (row[columnMap.employmentStatus] || '').trim(),
          employmentStatus2: (row[columnMap.employmentStatus2] || '').trim(),
          talId: (row[columnMap.talId] || '').trim(),
          status: 'active',
          isActive: true,
          isVerified: false, // Start as unverified
          needsAddressVerification: !hasCompleteAddress, // Flag for address prompt on first login
          importRowNumber: i, // Track original row for debugging
          importDate: new Date().toISOString()
        };

        // Validate address fields if required
        if (requireAddressOnImport) {
          const addressErrors = [];
          if (!user.streetAddress) addressErrors.push('Street address');
          if (!user.city) addressErrors.push('City');
          if (!user.province) addressErrors.push('Province');
          if (!user.postalCode) addressErrors.push('Postal code');

          if (addressErrors.length > 0) {
            devLog(`Row ${i}: Missing address fields: ${addressErrors.join(', ')}`);
            skippedCount++;
            continue;
          }
        }

        // Validate essential fields
        if (user.displayName.trim() && user.email.includes('@')) {
          users.push(user);
          processedCount++;
        } else {
          devLog(`Skipping row ${i}: Invalid data -`, {
            name: user.displayName,
            email: user.email
          });
          skippedCount++;
        }

      } catch (error) {
        devError(`Error parsing row ${i}:`, error);
        skippedCount++;
        continue;
      }
    }

    devLog(`Import summary: ${processedCount} users processed, ${skippedCount} rows skipped`);
    
    if (users.length === 0) {
      throw new Error(`No valid users found in the sheet. Processed ${processedCount} rows, skipped ${skippedCount} rows.`);
    }

    return users;
  };

  const findColumnIndex = (headers, possibleNames) => {
    const normalizeHeader = (value) => String(value || '')
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    for (const name of possibleNames) {
      const normalizedName = normalizeHeader(name);
      const index = headers.findIndex(h => normalizeHeader(h).includes(normalizedName));
      if (index !== -1) return index;
    }
    return -1;
  };

  const cleanPhoneNumber = (value) => String(value || '')
    .trim()
    .replace(/^'/, '')
    .replace(/\*/g, '')
    .replace(/[^\d+]/g, '');

  const parseCSVText = (csvText) => {
    // Auto-detect delimiter from the first non-empty line.
    // Google Sheets exports differ by locale and download method:
    //   commas  → standard CSV
    //   tabs    → TSV (common when pasting from spreadsheet or some locales)
    //   semicolons → European CSV locale
    const firstLine = csvText.split(/\r?\n/).find(l => l.trim()) || '';
    const tabCount = (firstLine.match(/\t/g) || []).length;
    const commaCount = (firstLine.match(/,/g) || []).length;
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const delimiter = tabCount >= commaCount && tabCount >= semicolonCount && tabCount > 0 ? '\t'
      : semicolonCount > commaCount ? ';'
      : ',';

    const rows = [];
    let row = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];
      const nextChar = csvText[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        row.push(current.trim());
        current = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') i++;
        row.push(current.trim());
        if (row.some(cell => String(cell || '').trim() !== '')) rows.push(row);
        row = [];
        current = '';
      } else {
        current += char;
      }
    }

    row.push(current.trim());
    if (row.some(cell => String(cell || '').trim() !== '')) rows.push(row);

    return rows;
  };

  // Function to create CSV export with credentials
  const createCredentialsCSV = (credentialsData) => {
    const headers = ['Name', 'Email', 'Password', 'Role', 'Created Date', 'Import Row', 'Status'];
    const csvContent = [
      headers.join(','),
      ...credentialsData.map(user => {
        // Escape commas and quotes in data
        const escapeCsvField = (field) => {
          if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
            return `"${field.replace(/"/g, '""')}"`;
          }
          return field || '';
        };

        return [
          escapeCsvField(user.name),
          escapeCsvField(user.email),
          escapeCsvField(user.password),
          escapeCsvField(user.role),
          escapeCsvField(user.createdAt),
          user.rowNumber || '',
          'Created Successfully'
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `npi_bulk_import_credentials_${timestamp}_${credentialsData.length}users.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    devLog(`Credentials CSV downloaded: ${credentialsData.length} users`);
  };

  const createExistingUsersCSV = (existingUsersData) => {
    const headers = ['Name', 'Email', 'Status', 'Import Row'];
    const csvContent = [
      headers.join(','),
      ...existingUsersData.map(user => {
        const escapeCsvField = (field) => {
          if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
            return `"${field.replace(/"/g, '""')}"`;
          }
          return field || '';
        };
        return [
          escapeCsvField(user.name),
          escapeCsvField(user.email),
          escapeCsvField(user.status),
          user.rowNumber || ''
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `npi_existing_users_${timestamp}_${existingUsersData.length}users.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    devLog(`Existing users CSV downloaded: ${existingUsersData.length} users`);
  };

  const handleProcessSheetsImport = async () => {
    if (sheetsData.length === 0) {
      setError('No valid user data found to import');
      return;
    }

    // Validate admin password
    if (!importAdminPassword || importAdminPassword.trim() === '') {
      setError('Admin password is required for bulk import');
      return;
    }

    try {
      setImportProgress(0);
      setImportStatus(`Preparing to import ${sheetsData.length} users...`);

      let createdCount = 0;
      let errorCount = 0;
      let duplicateCount = 0;
      const credentialsData = []; // Store credentials for new accounts export
      const existingUsersData = []; // Store existing accounts for separate export
      const errors = []; // Track specific errors

      devLog(`Starting bulk import of ${sheetsData.length} users`);
      
      for (let i = 0; i < sheetsData.length; i++) {
        try {
          const userData = sheetsData[i];

          // Check for existing user by email
          const existingUser = allUsers.find(user =>
            user.email.toLowerCase() === userData.email.toLowerCase()
          );

          if (existingUser) {
            devLog(`User already exists in LMS: ${userData.email}`);
            duplicateCount++;
            existingUsersData.push({
              name: userData.displayName,
              email: userData.email,
              rowNumber: userData.importRowNumber,
              status: 'Already registered (LMS)'
            });
            continue;
          }

          // Create user with temporary password
          const tempPassword = generateUserFriendlyPassword();

          // Prepare userData object with password and assigned program/cohort
          const userDataWithPassword = {
            ...userData,
            password: tempPassword,
            tempPassword: tempPassword, // Store temporarily for email trigger
            needsCredentialsEmail: true,
            programId: importProgramId, // Assign selected program
            cohortId: importCohortId || null, // Assign selected cohort if any
            emailVerified: false, // New users start as unverified
            adminPassword: importAdminPassword, // Use admin password for verification
            bulkImport: true, // Flag for bulk import
            importBatch: new Date().toISOString() // Track import batch
          };

          try {
            await createUser(userDataWithPassword);
          } catch (createErr) {
            if (createErr.code === 'auth/email-already-in-use') {
              // Email exists in Firebase Auth from another app (e.g. Launchpad)
              devLog(`User exists in Firebase Auth (Launchpad): ${userData.email}`);
              duplicateCount++;
              existingUsersData.push({
                name: userData.displayName,
                email: userData.email,
                rowNumber: userData.importRowNumber,
                status: 'Already registered (Launchpad)'
              });
              continue;
            }
            throw createErr;
          }

          delete userDataWithPassword.password;
          delete userDataWithPassword.tempPassword;
          delete userDataWithPassword.adminPassword;

          // Store credentials for export (email sent automatically via onUserCreated trigger)
          credentialsData.push({
            name: userData.displayName,
            email: userData.email,
            password: tempPassword,
            role: userData.role,
            createdAt: new Date().toLocaleString(),
            rowNumber: userData.importRowNumber
          });

          createdCount++;

          const progress = Math.round(((i + 1) / sheetsData.length) * 100);
          setImportProgress(progress);
          setImportStatus(`Processing user ${i + 1}/${sheetsData.length}: ${userData.displayName} (${createdCount} created, ${errorCount} errors, ${duplicateCount} duplicates)`);

          // Small delay to avoid rate limiting Firebase
          await new Promise(resolve => setTimeout(resolve, 150));

        } catch (error) {
          devError(`Error creating user ${sheetsData[i]?.email || 'unknown'}:`, error);
          errorCount++;
          errors.push({
            row: sheetsData[i]?.importRowNumber || i + 1,
            email: sheetsData[i]?.email || 'unknown',
            error: error.message
          });

          // Continue with other users even if one fails
          const progress = Math.round(((i + 1) / sheetsData.length) * 100);
          setImportProgress(progress);
        }
      }
      
      // Generate comprehensive import summary
      const summary = {
        total: sheetsData.length,
        created: createdCount,
        errors: errorCount,
        duplicates: duplicateCount,
        processed: createdCount + errorCount + duplicateCount
      };

      setImportStatus(
        `Import complete! Created: ${summary.created}, Already registered: ${summary.duplicates}, Errors: ${summary.errors}, Total processed: ${summary.processed}/${summary.total}`
      );

      // Log detailed summary
      devLog('Bulk import summary:', summary);
      if (errors.length > 0) {
        devLog('Import errors:', errors);
      }

      // Send welcome email to existing users and download their CSV
      if (existingUsersData.length > 0) {
        try {
          setImportStatus(`Sending welcome emails to ${existingUsersData.length} existing user(s)...`);
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const fns = getFunctions();
          const sendEmailFn = httpsCallable(fns, 'sendScheduledEmail');
          for (const existing of existingUsersData) {
            await sendEmailFn({
              templateType: 'welcome_existing_user',
              recipients: [existing.email],
              templateData: { userName: existing.name }
            });
          }
        } catch (emailErr) {
          devError('Error sending welcome emails to existing users:', emailErr);
        }
        createExistingUsersCSV(existingUsersData);
      }

      // Create and download credentials CSV for newly created accounts
      if (credentialsData.length > 0) {
        createCredentialsCSV(credentialsData);
        showNotification(
          `Import completed! ${summary.created} new user(s) created. ${existingUsersData.length > 0 ? `${existingUsersData.length} existing user(s) emailed and listed in separate CSV.` : ''} Credentials CSV downloaded.`,
          summary.created > 0 ? 'success' : 'warning'
        );
      } else if (existingUsersData.length > 0) {
        showNotification(
          `Import completed! All ${existingUsersData.length} user(s) were already registered. Welcome emails sent. Existing users CSV downloaded.`,
          'info'
        );
      }

      // Show error details if any
      if (errors.length > 0 && errors.length <= 10) {
        devLog('First 10 import errors:', errors.slice(0, 10));
      }
      
      // Refresh the user list
      await loadUsers();
      
      // Close modal after delay
      setTimeout(() => {
        setShowGoogleSheetsImport(false);
        setImportProgress(0);
        setImportStatus('');
        setSheetsData([]);
        setGoogleSheetsUrl('');
        setImportAdminPassword('');
      }, 3000);
      
    } catch (error) {
      setError('Import failed: ' + error.message);
    }
  };


  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <AdminNavbar currentPage="users" />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">User Management</h1>
              <p className="mission-text mb-4">
                Empowering learners through comprehensive user lifecycle management
              </p>
              <p className="mb-0">
                Manage students, facilitators, and administrators with advanced cohort management, 
                status tracking, and document handling capabilities.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {/* Success/Error Messages */}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-4">
            <Alert.Heading>Success!</Alert.Heading>
            {success}
          </Alert>
        )}
        
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
            <Alert.Heading>Error!</Alert.Heading>
            {error}
          </Alert>
        )}

        {/* New User Credentials Table */}
        {newUserCredentials && (
          <Alert variant="info" dismissible onClose={() => setNewUserCredentials(null)} className="mb-4">
            <Alert.Heading>New User Credentials Created</Alert.Heading>
            <p>Please save these credentials securely. The user will also receive them via email.</p>
            <Table striped bordered hover size="sm" className="mt-3 mb-0">
              <tbody>
                <tr>
                  <td><strong>Name:</strong></td>
                  <td>{newUserCredentials.name}</td>
                </tr>
                <tr>
                  <td><strong>Email:</strong></td>
                  <td>{newUserCredentials.email}</td>
                </tr>
                <tr>
                  <td><strong>Password:</strong></td>
                  <td>
                    <code>{newUserCredentials.password}</code>
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      className="ms-2"
                      onClick={() => {
                        navigator.clipboard.writeText(newUserCredentials.password);
                        showNotification('Password copied to clipboard!', 'success');
                      }}
                    >
                      Copy
                    </Button>
                  </td>
                </tr>
                <tr>
                  <td><strong>Role:</strong></td>
                  <td><span className="badge bg-primary">{newUserCredentials.role}</span></td>
                </tr>
                <tr>
                  <td><strong>Created:</strong></td>
                  <td>{newUserCredentials.createdAt}</td>
                </tr>
              </tbody>
            </Table>
          </Alert>
        )}
        
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2>System Users</h2>
                <p className="text-muted mb-0">Manage users and their permissions across all programs</p>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <Button variant="info" onClick={() => setShowGoogleSheetsImport(true)}>
                  Import Google Sheets
                </Button>
                <Button variant="success" onClick={handleExportPDF}>
                  Export PDF
                </Button>
                <Button variant="outline-success" onClick={handleExportFilteredSpreadsheet}>
                  Export Filtered Spreadsheet
                </Button>
                <Button variant="primary" onClick={() => {
                  setEditingUser(null);
                  setUserModalError('');
                  setNewUser({
                    displayName: '',
                    email: '',
                    password: '',
                    role: 'learner',
                    cohortId: '',
                    programId: '',
                    adminPassword: '',
                    alternateEmails: []
                  });
                  setShowUserModal(true);
                }}>
                  Add New User
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-2">
          <Col md={2}>
            <Form.Group>
              <Form.Label>Search Users</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Role</Form.Label>
              <Form.Control
                as="select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="facilitator">Facilitator</option>
                <option value="qa">QA</option>
                <option value="assessor">Assessor</option>
                <option value="learner">Learner</option>
                <option value="external_facilitator_limited">External Facilitator (Limited)</option>
                <option value="external_viewer_reporting">External Viewer (Reporting)</option>
                <option value="external_facilitator">External Facilitator</option>
                <option value="external_viewer">External Viewer</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Control
                as="select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Program</Form.Label>
              <Form.Control
                as="select"
                value={selectedProgram}
                onChange={(e) => {
                  setSelectedProgram(e.target.value);
                  setSelectedCohort('all');
                }}
              >
                <option value="all">All Programs</option>
                {allPrograms.map(program => (
                  <option key={program.id} value={program.id}>
                    {program.name || program.programName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Cohort</Form.Label>
              <Form.Control
                as="select"
                value={selectedCohort}
                onChange={(e) => setSelectedCohort(e.target.value)}
              >
                <option value="all">All Cohorts</option>
                {allCohorts
                  .filter((cohort) => selectedProgram === 'all' || cohort.programId === selectedProgram)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map(cohort => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>Login</Form.Label>
              <Form.Control
                as="select"
                value={selectedEmailStatus}
                onChange={(e) => setSelectedEmailStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="verified">Logged In</option>
                <option value="unverified">Never Logged In</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>Docs</Form.Label>
              <Form.Control
                as="select"
                value={selectedDocumentStatus}
                onChange={(e) => {
                  setSelectedDocumentStatus(e.target.value);
                  // Clear URL parameter when changing filter manually
                  if (searchParams.get('filter')) {
                    setSearchParams({});
                  }
                }}
              >
                <option value="all">All</option>
                <option value="overdue">Overdue</option>
                <option value="incomplete">Incomplete</option>
                <option value="complete">Complete</option>
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <Button variant="outline-secondary" onClick={() => {
                setSelectedRole('all');
                setSelectedStatus('all');
                setSelectedProgram('all');
                setSelectedCohort('all');
                setSelectedDocumentStatus('all');
                setSelectedEmailStatus('all');
                setSearchTerm('');
                setSearchParams({});
              }}>
                Clear Filters
              </Button>
              <Button 
                variant={showDeletedUsers ? 'secondary' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowDeletedUsers(!showDeletedUsers)}
              >
                {showDeletedUsers ? 'Show Active Users' : `Recently Deleted (${deletedUsers.length})`}
              </Button>
              <small className="text-muted ms-auto">
                Showing {currentUsers.length} of {filteredUsers.length} users
              </small>
            </div>
          </Col>
        </Row>

        {/* Bulk Actions Bar */}
        {selectedUserIds.length > 0 && (
          <Alert variant="info" className="d-flex justify-content-between align-items-center mb-3">
            <span>
              <strong>{selectedUserIds.length}</strong> learner(s) selected
            </span>
            <div>
              <Button
                variant="primary"
                size="sm"
                className="me-2"
                onClick={() => setShowBulkCohortAssign(true)}
              >
                Assign to Cohort
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setSelectedUserIds([])}
              >
                Clear Selection
              </Button>
            </div>
          </Alert>
        )}

        {/* Users Table */}
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <Form.Check
                      type="checkbox"
                      checked={(() => {
                        const learnersOnPage = currentUsers.filter(u => u.role === 'learner');
                        return learnersOnPage.length > 0 && learnersOnPage.every(u => selectedUserIds.includes(u.uid));
                      })()}
                      onChange={toggleSelectAllLearners}
                      title="Select all learners on this page"
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Program/Cohort</th>
                  <th>Progress</th>
                  <th>Documents</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(showDeletedUsers ? deletedUsers : currentUsers).map((user) => {
                  const progress = getUserProgress(user.uid);
                  const isLoginUnverified = !hasLoggedIn(user);
                  
                  return (
                    <tr
                      key={user.uid}
                      className={isLoginUnverified ? 'table-secondary' : ''}
                      style={{
                        opacity: isLoginUnverified ? 0.6 : 1,
                        fontStyle: isLoginUnverified ? 'italic' : 'normal'
                      }}
                    >
                      <td>
                        {user.role === 'learner' && (
                          <Form.Check
                            type="checkbox"
                            checked={selectedUserIds.includes(user.uid)}
                            onChange={() => toggleUserSelection(user.uid)}
                          />
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{ width: '32px', height: '32px', fontSize: '14px' }}
                          >
                            {user.displayName.charAt(0)}
                          </div>
                          <div>
                            <strong>{user.displayName}</strong>
                            {user.alternateEmails && user.alternateEmails.length > 0 && (
                              <div>
                                <small className="text-muted">+{user.alternateEmails.length} alt email(s)</small>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="d-flex align-items-center">
                            {user.email}
                            {isLoginUnverified ? (
                              <span 
                                className="badge bg-warning text-dark ms-2" 
                                title="User has never logged in"
                              >
                                Never Logged In
                              </span>
                            ) : (
                              <span 
                                className="badge bg-success ms-2" 
                                title="User has logged in"
                              >
                                Logged In
                              </span>
                            )}
                          </div>
                          {user.alternateEmails && user.alternateEmails.length > 0 && (
                            <div className="mt-1">
                              {user.alternateEmails.map((email, idx) => (
                                <small key={idx} className="text-muted d-block">{email}</small>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{user.phoneNumber || user.alternateNumber || 'N/A'}</td>
                      <td>
                        <Badge bg={getRoleBadgeColor(user.role)}>
                          {user.role}
                        </Badge>
                      </td>
                      <td>
                        <div>
                          <Badge bg={getStatusBadgeColor(user.status)}>
                            {statusOptions.find(opt => opt.value === user.status)?.label || user.status}
                          </Badge>
                          {user.statusReason && (
                            <div className="mt-1">
                              <small className="text-muted">
                                {user.statusReason}
                              </small>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {user.role === 'learner' ? (
                          <div>
                            <small className="fw-bold">{getProgramName(user.programId)}</small>
                            <br />
                            <small className="text-muted">{getCohortName(user.cohortId)}</small>
                          </div>
                        ) : user.assignedPrograms && user.assignedPrograms.length > 0 ? (
                          <div>
                            <small className="fw-bold">
                              {user.assignedPrograms.map(pid => getProgramName(pid)).join(', ')}
                            </small>
                            {user.assignedCohorts && user.assignedCohorts.length > 0 ? (
                              <>
                                <br />
                                <small className="text-muted">
                                  {user.assignedCohorts.map(cid => getCohortName(cid)).join(', ')}
                                </small>
                              </>
                            ) : user.managedCohorts && user.managedCohorts.length > 0 ? (
                              <>
                                <br />
                                <small className="text-muted">Manages {user.managedCohorts.length} cohort(s)</small>
                              </>
                            ) : null}
                          </div>
                        ) : user.programId ? (
                          <div>
                            <small className="fw-bold">{getProgramName(user.programId)}</small>
                          </div>
                        ) : user.managedCohorts && user.managedCohorts.length > 0 ? (
                          <div>
                            <small className="text-muted">Manages {user.managedCohorts.length} cohort(s)</small>
                          </div>
                        ) : (
                          <small className="text-muted">No Program</small>
                        )}
                      </td>
                      <td>
                        {user.role === 'learner' ? (
                          <div>
                            <div className="d-flex justify-content-between mb-1">
                              <small>{progress.completed}/{progress.enrolled}</small>
                              <small>{progress.progress}%</small>
                            </div>
                            <div className="progress" style={{ height: '6px' }}>
                              <div 
                                className="progress-bar" 
                                role="progressbar" 
                                style={{ width: `${progress.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td>
                        {user.role === 'learner' ? (
                          (() => {
                            const docStatus = getDocumentCompletionStatus(user);
                            const isOverdue = hasOverdueDocuments(user);
                            return (
                              <div>
                                <div className="d-flex justify-content-between mb-1">
                                  <small>{docStatus.completed}/{docStatus.total}</small>
                                  <small>{docStatus.percentage}%</small>
                                </div>
                                <div className="progress" style={{ height: '6px' }}>
                                  <div 
                                    className={`progress-bar ${isOverdue ? 'bg-danger' : docStatus.percentage === 100 ? 'bg-success' : 'bg-warning'}`}
                                    role="progressbar" 
                                    style={{ width: `${docStatus.percentage}%` }}
                                  ></div>
                                </div>
                                {isOverdue && (
                                  <small className="text-danger fw-bold">Overdue!</small>
                                )}
                              </div>
                            );
                          })()
                        ) : 'N/A'}
                      </td>
                      <td>
                        {(() => {
                          // Prioritize createdAt (actual account creation from Firebase Auth)
                          const dateToShow = user.createdAt || user.enrolledDate || user.deletedDate;
                          if (!dateToShow) return 'N/A';

                          try {
                            // Handle Firebase Timestamp objects
                            const date = dateToShow.toDate ? dateToShow.toDate() : new Date(dateToShow);
                            return date.toLocaleDateString();
                          } catch (error) {
                            return 'Invalid Date';
                          }
                        })()}
                      </td>
                      <td>
                        {showDeletedUsers ? (
                          <div className="d-flex gap-1">
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              onClick={() => handleRestoreUser(user)}
                            >
                              Restore
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handlePermanentDelete(user.uid)}
                            >
                              Delete
                            </Button>
                          </div>
                        ) : (
                          <div className="d-flex gap-1">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleEditUser(user)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleViewUserDetails(user)}
                            >
                              View
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleSecuritySettings(user)}
                            >
                              Security
                            </Button>
                            {user.role === 'learner' && (
                              <Button 
                                variant="outline-warning" 
                                size="sm"
                                onClick={() => handleCohortTransfer(user.uid)}
                              >
                                Transfer
                              </Button>
                            )}
                            {user.uid !== currentUser.uid && (
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => handleDeleteUser(user)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
                  
                  {[...Array(totalPages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === currentPage}
                      onClick={() => paginate(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  
                  <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
                  <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* User Modal */}
        <Modal show={showUserModal} onHide={() => { setShowUserModal(false); setUserModalError(''); }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingUser ? 'Edit User' : 'Add New User'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {userModalError && (
              <Alert variant="danger" className="modal-error-alert" dismissible onClose={() => setUserModalError('')}>
                <Alert.Heading>{editingUser ? 'User Could Not Be Updated' : 'User Could Not Be Created'}</Alert.Heading>
                <div>{userModalError}</div>
              </Alert>
            )}
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Display Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter display name"
                      value={editingUser ? editingUser.displayName : newUser.displayName}
                      onChange={(e) => editingUser ? 
                        setEditingUser({...editingUser, displayName: e.target.value}) : 
                        setNewUser({...newUser, displayName: e.target.value})
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email"
                      value={editingUser ? editingUser.email : newUser.email}
                      onChange={(e) => editingUser ? 
                        setEditingUser({...editingUser, email: e.target.value}) : 
                        setNewUser({...newUser, email: e.target.value})
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Phone Number Row */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter phone number"
                      value={editingUser ? editingUser.phoneNumber : newUser.phoneNumber}
                      onChange={(e) => editingUser ?
                        setEditingUser({...editingUser, phoneNumber: e.target.value}) :
                        setNewUser({...newUser, phoneNumber: e.target.value})
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Alternate Number</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter alternate number (optional)"
                      value={editingUser ? editingUser.alternateNumber : newUser.alternateNumber}
                      onChange={(e) => editingUser ?
                        setEditingUser({...editingUser, alternateNumber: e.target.value}) :
                        setNewUser({...newUser, alternateNumber: e.target.value})
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Password field for new users only */}
              {!editingUser && (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="password"
                          placeholder="Enter password or generate"
                          value={newUser.password}
                          onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        />
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => setNewUser({...newUser, password: generatePassword()})}
                        >
                          Generate
                        </Button>
                        {newUser.password && (
                          <Button 
                            variant="outline-primary" 
                            onClick={() => copyToClipboard(newUser.password)}
                            title="Copy password to clipboard"
                          >
                            Copy
                          </Button>
                        )}
                      </div>
                      <Form.Text className="text-muted">
                        Leave empty to auto-generate a secure password
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Admin Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter your admin password"
                        value={newUser.adminPassword}
                        onChange={(e) => setNewUser({...newUser, adminPassword: e.target.value})}
                      />
                      <Form.Text className="text-muted">
                        Required to confirm user creation
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Role</Form.Label>
                    <Form.Control
                      as="select"
                      value={editingUser ? editingUser.role : newUser.role}
                      onChange={(e) => editingUser ?
                        setEditingUser({...editingUser, role: e.target.value}) :
                        setNewUser({...newUser, role: e.target.value})
                      }
                    >
                      <option value="learner">Learner</option>
                      <option value="assessor">Assessor</option>
                      <option value="facilitator">Facilitator</option>
                      <option value="qa">QA (Quality Assurance)</option>
                      <option value="admin">Admin</option>
                      <option value="external_facilitator_limited">External Facilitator (Limited)</option>
                      <option value="external_viewer_reporting">External Viewer (Reporting)</option>
                      <option value="external_facilitator">External Facilitator</option>
                      <option value="external_viewer">External Viewer</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      as="select"
                      value={editingUser ? editingUser.status : newUser.status}
                      onChange={(e) => editingUser ? 
                        setEditingUser({...editingUser, status: e.target.value}) : 
                        setNewUser({...newUser, status: e.target.value})
                      }
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Additional Fields for Learners */}
              {(editingUser?.role === 'learner' || newUser.role === 'learner') && (
                <>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program</Form.Label>
                        <Form.Control
                          as="select"
                          value={editingUser ? editingUser.programId : newUser.programId}
                          onChange={(e) => editingUser ?
                            setEditingUser({...editingUser, programId: e.target.value}) :
                            setNewUser({...newUser, programId: e.target.value})
                          }
                        >
                          <option value="">Select Program</option>
                          {allPrograms.map(program => (
                            <option key={program.id} value={program.id}>
                              {program.name}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Cohort</Form.Label>
                        <Form.Control
                          as="select"
                          value={editingUser ? editingUser.cohortId : newUser.cohortId}
                          onChange={(e) => editingUser ? 
                            setEditingUser({...editingUser, cohortId: e.target.value}) : 
                            setNewUser({...newUser, cohortId: e.target.value})
                          }
                        >
                          <option value="">Select Cohort</option>
                          {allCohorts.filter(cohort => 
                            (!editingUser?.programId && !newUser.programId) ||
                            cohort.programId === (editingUser ? editingUser.programId : newUser.programId)
                          ).map(cohort => (
                            <option key={cohort.id} value={cohort.id}>
                              {cohort.name}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  {/* Status Reason */}
                  {((editingUser?.status !== 'active') || (newUser.status !== 'active')) && (
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Status Reason</Form.Label>
                          <Form.Control
                            as="select"
                            value={editingUser ? editingUser.statusReason : newUser.statusReason}
                            onChange={(e) => editingUser ? 
                              setEditingUser({...editingUser, statusReason: e.target.value}) : 
                              setNewUser({...newUser, statusReason: e.target.value})
                            }
                          >
                            <option value="">Select Reason</option>
                            {(() => {
                              const currentStatus = editingUser ? editingUser.status : newUser.status;
                              const reasonsArray = statusReasons[currentStatus] || [];
                              return reasonsArray.map((reason, index) => (
                                <option key={index} value={reason}>
                                  {reason}
                                </option>
                              ));
                            })()}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Additional Details</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Additional details about status"
                            value={editingUser ? editingUser.statusReasonDetail : newUser.statusReasonDetail}
                            onChange={(e) => editingUser ? 
                              setEditingUser({...editingUser, statusReasonDetail: e.target.value}) : 
                              setNewUser({...newUser, statusReasonDetail: e.target.value})
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                </>
              )}

              {/* Additional Fields for Facilitators and Admins */}
              {(editingUser?.role === 'facilitator' || newUser.role === 'facilitator' ||
                editingUser?.role === 'admin' || newUser.role === 'admin') && (
                <>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Assigned Programs</Form.Label>
                        <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {allPrograms.map(program => (
                            <Form.Check
                              key={program.id}
                              type="checkbox"
                              id={`program-${program.id}`}
                              label={program.name}
                              checked={editingUser ?
                                (editingUser.assignedPrograms || []).includes(program.id) :
                                (newUser.assignedPrograms || []).includes(program.id)
                              }
                              onChange={(e) => {
                                const programId = program.id;
                                const isChecked = e.target.checked;

                                if (editingUser) {
                                  const currentPrograms = editingUser.assignedPrograms || [];
                                  const updatedPrograms = isChecked
                                    ? [...currentPrograms, programId]
                                    : currentPrograms.filter(id => id !== programId);
                                  setEditingUser({...editingUser, assignedPrograms: updatedPrograms});
                                } else {
                                  const currentPrograms = newUser.assignedPrograms || [];
                                  const updatedPrograms = isChecked
                                    ? [...currentPrograms, programId]
                                    : currentPrograms.filter(id => id !== programId);
                                  setNewUser({...newUser, assignedPrograms: updatedPrograms});
                                }
                              }}
                            />
                          ))}
                          {allPrograms.length === 0 && (
                            <small className="text-muted">No programs available</small>
                          )}
                        </div>
                        <Form.Text className="text-muted">
                          Select which programs this {editingUser?.role || newUser.role} can manage
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Assigned Cohorts (Optional)</Form.Label>
                        <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {allCohorts
                            .filter(cohort => {
                              const selectedPrograms = editingUser?.assignedPrograms || newUser.assignedPrograms || [];
                              return selectedPrograms.length === 0 || selectedPrograms.includes(cohort.programId);
                            })
                            .map(cohort => (
                            <Form.Check
                              key={cohort.id}
                              type="checkbox"
                              id={`facilitator-cohort-${cohort.id}`}
                              label={`${cohort.name} (${getProgramName(cohort.programId)})`}
                              checked={editingUser ?
                                (editingUser.assignedCohorts || []).includes(cohort.id) :
                                (newUser.assignedCohorts || []).includes(cohort.id)
                              }
                              onChange={(e) => {
                                const cohortId = cohort.id;
                                const isChecked = e.target.checked;

                                if (editingUser) {
                                  const currentCohorts = editingUser.assignedCohorts || [];
                                  const updatedCohorts = isChecked
                                    ? [...currentCohorts, cohortId]
                                    : currentCohorts.filter(id => id !== cohortId);
                                  setEditingUser({...editingUser, assignedCohorts: updatedCohorts});
                                } else {
                                  const currentCohorts = newUser.assignedCohorts || [];
                                  const updatedCohorts = isChecked
                                    ? [...currentCohorts, cohortId]
                                    : currentCohorts.filter(id => id !== cohortId);
                                  setNewUser({...newUser, assignedCohorts: updatedCohorts});
                                }
                              }}
                            />
                          ))}
                          {allCohorts.filter(c => {
                            const selectedPrograms = editingUser?.assignedPrograms || newUser.assignedPrograms || [];
                            return selectedPrograms.length === 0 || selectedPrograms.includes(c.programId);
                          }).length === 0 && (
                            <small className="text-muted">Select programs first to see available cohorts</small>
                          )}
                        </div>
                        <Form.Text className="text-muted">
                          Select specific cohorts to manage. Leave empty to access all cohorts within selected programs.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}

              {/* Additional Fields for External Facilitators and External Viewers */}
              {(editingUser?.role === 'external_facilitator' || newUser.role === 'external_facilitator' ||
                editingUser?.role === 'external_facilitator_limited' || newUser.role === 'external_facilitator_limited' ||
                editingUser?.role === 'external_viewer' || newUser.role === 'external_viewer' ||
                editingUser?.role === 'external_viewer_reporting' || newUser.role === 'external_viewer_reporting') && (
                <>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Assigned Programs</Form.Label>
                        <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {allPrograms.map(program => (
                            <Form.Check
                              key={program.id}
                              type="checkbox"
                              id={`external-program-${program.id}`}
                              label={program.name}
                              checked={editingUser ?
                                (editingUser.assignedPrograms || []).includes(program.id) :
                                (newUser.assignedPrograms || []).includes(program.id)
                              }
                              onChange={(e) => {
                                const programId = program.id;
                                const isChecked = e.target.checked;

                                if (editingUser) {
                                  const currentPrograms = editingUser.assignedPrograms || [];
                                  const updatedPrograms = isChecked
                                    ? [...currentPrograms, programId]
                                    : currentPrograms.filter(id => id !== programId);
                                  setEditingUser({...editingUser, assignedPrograms: updatedPrograms});
                                } else {
                                  const currentPrograms = newUser.assignedPrograms || [];
                                  const updatedPrograms = isChecked
                                    ? [...currentPrograms, programId]
                                    : currentPrograms.filter(id => id !== programId);
                                  setNewUser({...newUser, assignedPrograms: updatedPrograms});
                                }
                              }}
                            />
                          ))}
                          {allPrograms.length === 0 && (
                            <small className="text-muted">No programs available</small>
                          )}
                        </div>
                        <Form.Text className="text-muted">
                          Select which programs this {(['external_facilitator', 'external_facilitator_limited'].includes(editingUser?.role || newUser.role))
                            ? 'external facilitator'
                            : 'external viewer'} can access
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Assigned Cohorts (Optional)</Form.Label>
                        <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {allCohorts.filter(cohort => {
                            const selectedPrograms = editingUser
                              ? (editingUser.assignedPrograms || [])
                              : (newUser.assignedPrograms || []);
                            return selectedPrograms.length === 0 || selectedPrograms.includes(cohort.programId);
                          }).map(cohort => (
                            <Form.Check
                              key={cohort.id}
                              type="checkbox"
                              id={`external-cohort-${cohort.id}`}
                              label={`${cohort.name} (${getProgramName(cohort.programId)})`}
                              checked={editingUser ?
                                (editingUser.assignedCohorts || []).includes(cohort.id) :
                                (newUser.assignedCohorts || []).includes(cohort.id)
                              }
                              onChange={(e) => {
                                const cohortId = cohort.id;
                                const isChecked = e.target.checked;

                                if (editingUser) {
                                  const currentCohorts = editingUser.assignedCohorts || [];
                                  const updatedCohorts = isChecked
                                    ? [...currentCohorts, cohortId]
                                    : currentCohorts.filter(id => id !== cohortId);
                                  setEditingUser({...editingUser, assignedCohorts: updatedCohorts});
                                } else {
                                  const currentCohorts = newUser.assignedCohorts || [];
                                  const updatedCohorts = isChecked
                                    ? [...currentCohorts, cohortId]
                                    : currentCohorts.filter(id => id !== cohortId);
                                  setNewUser({...newUser, assignedCohorts: updatedCohorts});
                                }
                              }}
                            />
                          ))}
                          {allCohorts.length === 0 && (
                            <small className="text-muted">No cohorts available</small>
                          )}
                        </div>
                        <Form.Text className="text-muted">
                          Optionally restrict access to specific cohorts. Leave empty to allow access to all cohorts within selected programs.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}

              {/* Address Information Section */}
              <Row>
                <Col md={12}>
                  <hr className="my-4" />
                  <h6 className="mb-3">Address Information</h6>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Street Address</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter street address"
                      value={editingUser ? editingUser.streetAddress : newUser.streetAddress}
                      onChange={(e) => editingUser ?
                        setEditingUser({...editingUser, streetAddress: e.target.value}) :
                        setNewUser({...newUser, streetAddress: e.target.value})
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter city"
                      value={editingUser ? editingUser.city : newUser.city}
                      onChange={(e) => editingUser ?
                        setEditingUser({...editingUser, city: e.target.value}) :
                        setNewUser({...newUser, city: e.target.value})
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Province</Form.Label>
                    <Form.Control
                      as="select"
                      value={editingUser ? editingUser.province : newUser.province}
                      onChange={(e) => editingUser ?
                        setEditingUser({...editingUser, province: e.target.value}) :
                        setNewUser({...newUser, province: e.target.value})
                      }
                    >
                      <option value="">Select province...</option>
                      {SOUTH_AFRICAN_PROVINCES.map(prov => (
                        <option key={prov} value={prov}>{prov}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="4 digits"
                      value={editingUser ? editingUser.postalCode : newUser.postalCode}
                      onChange={(e) => editingUser ?
                        setEditingUser({...editingUser, postalCode: e.target.value}) :
                        setNewUser({...newUser, postalCode: e.target.value})
                      }
                      maxLength={4}
                      pattern="\d{4}"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={editingUser ? handleSaveUser : handleAddUser} disabled={loading}>
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to move user <strong>{userToDelete?.displayName}</strong> to recently deleted? You can restore them later if needed.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              Delete User
            </Button>
          </Modal.Footer>
        </Modal>

        {/* User Details Modal */}
        <Modal show={showUserDetails} onHide={() => setShowUserDetails(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>User Details - {selectedUser?.displayName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedUser && (
              <Row>
                <Col md={8}>
                  <Card className="mb-3">
                    <Card.Header>
                      <h5>Personal Information</h5>
                    </Card.Header>
                    <Card.Body>
                      <p><strong>Name:</strong> {selectedUser.displayName}</p>
                      <p><strong>Primary Email:</strong> {selectedUser.email}</p>
                      {selectedUser.alternateEmails && selectedUser.alternateEmails.length > 0 && (
                        <p><strong>Alternate Emails:</strong> {selectedUser.alternateEmails.join(', ')}</p>
                      )}
                      <p><strong>Role:</strong> <Badge bg={getRoleBadgeColor(selectedUser.role)}>{selectedUser.role}</Badge></p>
                      <p><strong>Status:</strong> <Badge bg={getStatusBadgeColor(selectedUser.status)}>
                        {statusOptions.find(opt => opt.value === selectedUser.status)?.label || selectedUser.status}
                      </Badge></p>
                      {selectedUser.statusReason && (
                        <p><strong>Status Reason:</strong> {selectedUser.statusReason}</p>
                      )}
                      {selectedUser.statusReasonDetail && (
                        <p><strong>Details:</strong> {selectedUser.statusReasonDetail}</p>
                      )}
                      <p><strong>Enrolled Date:</strong> {new Date(selectedUser.enrolledDate).toLocaleDateString()}</p>
                    </Card.Body>
                  </Card>
                  
                  {selectedUser.role === 'learner' && (
                    <Card className="mb-3">
                      <Card.Header>
                        <h5>Academic Information</h5>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Program:</strong> {getProgramName(selectedUser.programId)}</p>
                        <p><strong>Cohort:</strong> {getCohortName(selectedUser.cohortId)}</p>
                        {selectedUser.transferHistory && selectedUser.transferHistory.length > 0 && (
                          <div>
                            <strong>Transfer History:</strong>
                            {selectedUser.transferHistory.map((transfer, idx) => (
                              <div key={idx} className="mt-1 p-2 border rounded">
                                <small>
                                  From {getCohortName(transfer.fromCohortId)} to {getCohortName(transfer.toCohortId)}<br/>
                                  <em>Reason: {transfer.reason}</em><br/>
                                  <em>Date: {new Date(transfer.date).toLocaleDateString()}</em>
                                </small>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  )}

                  {(
                    selectedUser.role === 'facilitator' ||
                    selectedUser.role === 'external_facilitator' ||
                    selectedUser.role === 'external_facilitator_limited' ||
                    selectedUser.role === 'external_viewer' ||
                    selectedUser.role === 'external_viewer_reporting'
                  ) && (
                    <Card className="mb-3">
                      <Card.Header>
                        <h5>Assigned Programs & Cohorts</h5>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Programs:</strong> {selectedUser.assignedPrograms && selectedUser.assignedPrograms.length > 0
                          ? selectedUser.assignedPrograms.map(pid => getProgramName(pid)).join(', ')
                          : 'None assigned'}</p>
                        <p><strong>Cohorts:</strong> {selectedUser.assignedCohorts && selectedUser.assignedCohorts.length > 0
                          ? selectedUser.assignedCohorts.map(cid => getCohortName(cid)).join(', ')
                          : selectedUser.managedCohorts && selectedUser.managedCohorts.length > 0
                            ? selectedUser.managedCohorts.map(cid => getCohortName(cid)).join(', ')
                            : 'All cohorts in assigned programs'}</p>
                      </Card.Body>
                    </Card>
                  )}
                </Col>

                <Col md={4}>
                  <Card>
                    <Card.Header>
                      <h5>Documents Status</h5>
                    </Card.Header>
                    <Card.Body>
                      {selectedUser.role === 'learner' ? (
                        (() => {
                          const userDocuments = selectedUser.documents || {};
                          const requiredCategories = Object.keys(DOCUMENT_CATEGORIES).filter(
                            key => DOCUMENT_CATEGORIES[key].mandatory
                          );
                          const docCompletionStatus = getDocumentCompletionStatus(selectedUser);
                          const isOverdue = hasOverdueDocuments(selectedUser);
                          
                          return (
                            <div>
                              <div className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                  <small><strong>Overall Progress</strong></small>
                                  <small><strong>{docCompletionStatus.completed}/{docCompletionStatus.total} ({docCompletionStatus.percentage}%)</strong></small>
                                </div>
                                <div className="progress mb-2" style={{ height: '8px' }}>
                                  <div 
                                    className={`progress-bar ${isOverdue ? 'bg-danger' : docCompletionStatus.percentage === 100 ? 'bg-success' : 'bg-warning'}`}
                                    role="progressbar" 
                                    style={{ width: `${docCompletionStatus.percentage}%` }}
                                  ></div>
                                </div>
                                {isOverdue && (
                                  <Alert variant="danger" className="py-2">
                                    <small><strong>Overdue submissions</strong></small>
                                  </Alert>
                                )}
                              </div>
                              
                              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {requiredCategories.map((category) => {
                                  const categoryInfo = DOCUMENT_CATEGORIES[category];
                                  const document = userDocuments[category];
                                  const hasDocument = document && document.fileURL;
                                  
                                  return (
                                    <div key={category} className="mb-2 p-2 border rounded">
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                          <div className="fw-bold" style={{ fontSize: '0.9em' }}>
                                            {categoryInfo.name}
                                          </div>
                                          <div style={{ fontSize: '0.8em' }} className="text-muted">
                                            {categoryInfo.description}
                                          </div>
                                          {hasDocument && (
                                            <div style={{ fontSize: '0.8em' }} className="mt-1">
                                              <div>Status: <Badge bg={document.status === 'verified' ? 'success' : document.status === 'rejected' ? 'danger' : 'warning'}>
                                                {document.status || 'pending'}
                                              </Badge></div>
                                              {document.uploadDate && (
                                                <div className="text-muted">Uploaded: {new Date(document.uploadDate.toDate()).toLocaleDateString()}</div>
                                              )}
                                              {document.rejectionComment && (
                                                <div className="text-danger">Note: {document.rejectionComment}</div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <div className="d-flex flex-column align-items-end">
                                          {hasDocument ? (
                                            <Badge bg="success" className="mb-1">Submitted</Badge>
                                          ) : (
                                            <Badge bg="danger" className="mb-1">Missing</Badge>
                                          )}
                                          {hasDocument && (
                                            <Button 
                                              variant="outline-info" 
                                              size="sm"
                                              onClick={() => handleDownloadDocument(selectedUser, document)}
                                              title="Download"
                                            >
                                              ↓
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <p className="text-muted">Document tracking not applicable for {selectedUser.role}s</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserDetails(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Cohort Transfer Modal */}
        <Modal show={showCohortTransfer} onHide={() => setShowCohortTransfer(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Transfer Student to Another Cohort</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Target Cohort</Form.Label>
                <Form.Control
                  as="select"
                  value={transferData.targetCohortId}
                  onChange={(e) => setTransferData({...transferData, targetCohortId: e.target.value})}
                >
                  <option value="">Select Target Cohort</option>
                  {allCohorts.map(cohort => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name} ({getProgramName(cohort.programId)})
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Reason for Transfer</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={transferData.reason}
                  onChange={(e) => setTransferData({...transferData, reason: e.target.value})}
                  placeholder="Enter reason for transferring student to new cohort"
                />
              </Form.Group>
              <Alert variant="info">
                <strong>Note:</strong> All student documents will be transferred along with the student to the new cohort.
              </Alert>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCohortTransfer(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleTransferConfirm}
              disabled={!transferData.targetCohortId || !transferData.reason}
            >
              Transfer Student
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk Cohort Assignment Modal */}
        <Modal show={showBulkCohortAssign} onHide={() => !isBulkAssigning && setShowBulkCohortAssign(false)}>
          <Modal.Header closeButton={!isBulkAssigning}>
            <Modal.Title>Bulk Assign Learners to Cohort</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isBulkAssigning ? (
              <div className="text-center py-4">
                <h6>Assigning learners...</h6>
                <div className="progress mb-3">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${bulkAssignProgress}%` }}
                  >
                    {bulkAssignProgress}%
                  </div>
                </div>
                <small className="text-muted">Please wait while learners are being assigned...</small>
              </div>
            ) : (
              <Form>
                <Alert variant="info">
                  <strong>{selectedUserIds.length}</strong> learner(s) will be assigned to the selected program and cohort.
                </Alert>
                <Form.Group className="mb-3">
                  <Form.Label>Program</Form.Label>
                  <Form.Control
                    as="select"
                    value={bulkCohortData.programId}
                    onChange={(e) => setBulkCohortData({ programId: e.target.value, cohortId: '' })}
                  >
                    <option value="">Select Program</option>
                    {allPrograms.map(program => (
                      <option key={program.id} value={program.id}>
                        {program.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Cohort</Form.Label>
                  <Form.Control
                    as="select"
                    value={bulkCohortData.cohortId}
                    onChange={(e) => setBulkCohortData({ ...bulkCohortData, cohortId: e.target.value })}
                    disabled={!bulkCohortData.programId}
                  >
                    <option value="">Select Cohort</option>
                    {allCohorts
                      .filter(cohort => cohort.programId === bulkCohortData.programId)
                      .map(cohort => (
                        <option key={cohort.id} value={cohort.id}>
                          {cohort.name}
                        </option>
                      ))}
                  </Form.Control>
                  {bulkCohortData.programId && allCohorts.filter(c => c.programId === bulkCohortData.programId).length === 0 && (
                    <Form.Text className="text-danger">
                      No cohorts available for this program. Please create a cohort first.
                    </Form.Text>
                  )}
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowBulkCohortAssign(false)}
              disabled={isBulkAssigning}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleBulkCohortAssign}
              disabled={!bulkCohortData.programId || !bulkCohortData.cohortId || isBulkAssigning}
            >
              {isBulkAssigning ? 'Assigning...' : `Assign ${selectedUserIds.length} Learner(s)`}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Google Sheets Import Modal */}
        <Modal show={showGoogleSheetsImport} onHide={() => setShowGoogleSheetsImport(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Import Users from Google Sheets</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {importProgress > 0 && importStatus ? (
              <div>
                <h6>Import Progress</h6>
                <div className="mb-3">
                  <div className="progress">
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{width: `${importProgress}%`}}
                    >
                      {importProgress}%
                    </div>
                  </div>
                </div>
                <p className="text-muted">{importStatus}</p>
                
                {sheetsData.length > 0 && importProgress === 100 && (
                  <div className="mt-3">
                    <h6>Ready to Import ({sheetsData.length} users found)</h6>

                    {/* NPI Programme assignment */}
                    <Form.Group className="mb-2">
                      <Form.Label className="fw-semibold mb-1">
                        Assign all learners to NPI Programme <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Select
                          value={importProgramId}
                          onChange={(e) => setImportProgramId(e.target.value)}
                          className="flex-grow-1"
                        >
                          <option value="">Select programme for all learners...</option>
                          {allPrograms.map(program => (
                            <option key={program.id} value={program.id}>
                              {program.name || program.programName}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Select
                          value={importCohortId}
                          onChange={(e) => setImportCohortId(e.target.value)}
                          style={{ maxWidth: '220px' }}
                        >
                          <option value="">No cohort (optional)</option>
                          {allCohorts.filter(c => !importProgramId || c.programId === importProgramId).map(cohort => (
                            <option key={cohort.id} value={cohort.id}>
                              {cohort.name || cohort.cohortName}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      {!importProgramId && (
                        <Form.Text className="text-danger">Please select a programme before importing.</Form.Text>
                      )}
                    </Form.Group>

                    {(() => {
                      const existingEmails = new Set(allUsers.map(u => (u.email || '').toLowerCase()));
                      const newUsers = sheetsData.filter(u => !existingEmails.has((u.email || '').toLowerCase()));
                      const alreadyRegistered = sheetsData.filter(u => existingEmails.has((u.email || '').toLowerCase()));
                      return (
                        <Alert variant={alreadyRegistered.length > 0 ? 'info' : 'success'} className="mb-2 py-2">
                          <strong>{newUsers.length} new</strong> account(s) will be created and emailed credentials.
                          {alreadyRegistered.length > 0 && (
                            <> &nbsp;·&nbsp; <strong>{alreadyRegistered.length} already registered</strong> — will receive a welcome email to use existing credentials.</>
                          )}
                        </Alert>
                      );
                    })()}

                    <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px'}}>
                      <Table striped bordered size="sm" className="mb-0">
                        <thead style={{position: 'sticky', top: 0, background: '#fff', zIndex: 1}}>
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Province</th>
                            <th>Qualification (from CSV)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const existingEmails = new Set(allUsers.map(u => (u.email || '').toLowerCase()));
                            return sheetsData.map((user, index) => {
                              const isExisting = existingEmails.has((user.email || '').toLowerCase());
                              return (
                                <tr key={index}>
                                  <td className="text-muted">{index + 1}</td>
                                  <td>{user.displayName}</td>
                                  <td>{user.email}</td>
                                  <td>
                                    {isExisting
                                      ? <span className="badge bg-warning text-dark">Already registered</span>
                                      : <span className="badge bg-success">New</span>}
                                  </td>
                                  <td>{user.province || '—'}</td>
                                  <td>{user.programme || '—'}</td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <Alert variant="info">
                  <h6>Google Sheets Import Instructions:</h6>
                  <ul className="mb-0">
                    <li><strong>Supported columns:</strong> Name/Surname, Email Address, Identity Number, Gender, Race, Cell Number, Province, etc.</li>
                    <li><strong>Sheet must have link sharing enabled</strong></li>
                    <li><strong>Expected format:</strong> First row contains headers, data starts from second row</li>
                    <li>All imported users will be created as <strong>learners</strong></li>
                    <li>Passwords will be auto-generated and can be emailed to users</li>
                  </ul>
                </Alert>

                <div className="mb-3 p-3 border rounded" style={{ background: '#f8f9fa' }}>
                  <p className="fw-semibold mb-2">Import source</p>
                  <Form.Group className="mb-3">
                    <Form.Label>Option 1 — Google Sheets URL</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="url"
                        value={googleSheetsUrl}
                        onChange={(e) => { setGoogleSheetsUrl(e.target.value); setCsvFileName(''); setSheetsData([]); setImportProgress(0); setImportStatus(''); }}
                        placeholder="https://docs.google.com/spreadsheets/d/..."
                        disabled={!!csvFileName}
                      />
                      <Button
                        variant="outline-primary"
                        onClick={handleGoogleSheetsImport}
                        disabled={!googleSheetsUrl.trim() || !importAdminPassword.trim() || !importProgramId || !!csvFileName}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Load Sheet
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      The sheet must be shared with "Anyone with the link can view". If Google blocks the request, use Option 2.
                    </Form.Text>
                  </Form.Group>

                  <hr className="my-2" />

                  <Form.Group className="mb-0">
                    <Form.Label>Option 2 — Upload CSV file <span className="text-muted fw-normal">(most reliable)</span></Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="file"
                        accept=".csv"
                        onChange={(e) => { setGoogleSheetsUrl(''); handleCsvFileUpload(e.target.files?.[0]); e.target.value = ''; }}
                        disabled={!!googleSheetsUrl.trim()}
                      />
                      {csvFileName && (
                        <Button variant="outline-secondary" size="sm" onClick={() => { setCsvFileName(''); setSheetsData([]); setImportProgress(0); setImportStatus(''); }}>
                          Clear
                        </Button>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      In Google Sheets: <strong>File → Download → Comma Separated Values (.csv)</strong>, then select the downloaded file here.
                    </Form.Text>
                    {csvFileName && <div className="mt-1 text-success small">✓ {csvFileName}</div>}
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Program <span className="text-danger">*</span></Form.Label>
                  {!showCreateProgram ? (
                    <>
                      <div className="d-flex gap-2">
                        <Form.Select
                          value={importProgramId}
                          onChange={(e) => setImportProgramId(e.target.value)}
                          className="flex-grow-1"
                        >
                          <option value="">Select program for all learners...</option>
                          {allPrograms.map(program => (
                            <option key={program.id} value={program.id}>
                              {program.name || program.programName}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          variant="outline-success"
                          onClick={() => setShowCreateProgram(true)}
                        >
                          + Create New
                        </Button>
                      </div>
                      <Form.Text className="text-muted">
                        All imported learners will be assigned to this program.
                      </Form.Text>
                    </>
                  ) : (
                    <Card className="border-success">
                      <Card.Body>
                        <h6 className="text-success mb-3">Create New Program</h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={newProgramName}
                            onChange={(e) => setNewProgramName(e.target.value)}
                            placeholder="e.g., 4IR Skills Development 2025"
                            autoFocus
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Description (Optional)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={newProgramDescription}
                            onChange={(e) => setNewProgramDescription(e.target.value)}
                            placeholder="Brief description of the program..."
                          />
                        </Form.Group>
                        <div className="d-flex gap-2">
                          <Button
                            variant="success"
                            onClick={handleCreateProgramForImport}
                            disabled={!newProgramName.trim()}
                          >
                            Create Program
                          </Button>
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              setShowCreateProgram(false);
                              setNewProgramName('');
                              setNewProgramDescription('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Cohort (Optional)</Form.Label>
                  <Form.Select
                    value={importCohortId}
                    onChange={(e) => setImportCohortId(e.target.value)}
                  >
                    <option value="">No cohort (assign later)</option>
                    {allCohorts.map(cohort => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name || cohort.cohortName}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Optionally assign all imported learners to a specific cohort.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="requireAddressImport"
                    label="Require address fields in CSV (Street Address, City, Province, Postal Code)"
                    checked={requireAddressOnImport}
                    onChange={(e) => setRequireAddressOnImport(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    If checked, rows without complete address information will be skipped during import.
                    If unchecked, users can add address later or admins can update it.
                  </Form.Text>
                </Form.Group>

                {!importProgramId && (
                  <small className="text-danger">Please select a program above before loading data.</small>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowGoogleSheetsImport(false);
              setImportProgress(0);
              setImportStatus('');
              setSheetsData([]);
              setGoogleSheetsUrl('');
              setImportAdminPassword('');
              setImportProgramId('');
              setImportCohortId('');
              setShowCreateProgram(false);
              setNewProgramName('');
              setNewProgramDescription('');
              setCsvFileName('');
            }}>
              Cancel
            </Button>
            {sheetsData.length > 0 && importProgress === 100 && (
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <Form.Control
                  type="password"
                  value={importAdminPassword}
                  onChange={(e) => setImportAdminPassword(e.target.value)}
                  placeholder="Enter your admin password to confirm"
                  style={{ maxWidth: '280px' }}
                />
                <Button
                  variant="primary"
                  onClick={handleProcessSheetsImport}
                  disabled={!importAdminPassword.trim() || !importProgramId}
                >
                  Import {sheetsData.length} Users
                </Button>
              </div>
            )}
          </Modal.Footer>
        </Modal>

        {/* Enhanced User Security Modal */}
        <EnhancedUserSecurity
          user={securityUser}
          show={showSecurityModal}
          onHide={() => setShowSecurityModal(false)}
          onUpdate={() => {
            loadUsers();
            setShowSecurityModal(false);
          }}
        />
      </Container>
    </>
  );
};

export default UserManagement;
